import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Grid3x3, Download, Package, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  academyTrainingMatrix, academyBulkCertificatesZip, academyDeleteCertificate,
} from '../../../services/api';

const STATUS_COLORS = {
  not_started: 'bg-slate-100 text-slate-500',
  in_progress: 'bg-amber-100 text-amber-800',
  complete: 'bg-emerald-100 text-emerald-800',
  due_soon: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-rose-100 text-rose-800',
  not_assigned: 'bg-white text-slate-300',
};

/** Admin → Training Matrix. Employees × Courses grid with per-cell status. */
export default function TrainingMatrix() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('');
  const [status, setStatus] = useState('all');
  const [bulkCourse, setBulkCourse] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { setData(await academyTrainingMatrix()); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Could not load matrix'); }
    })();
  }, []);

  // Hooks MUST run in the same order every render, so `useMemo` sits
  // above the early return. It safely handles the pre-load `data==null`
  // state by returning an empty list.
  const visibleEmployees = useMemo(() => {
    if (!data) return [];
    const byName = data.employees.filter(e =>
      !filter || (e.name || '').toLowerCase().includes(filter.toLowerCase()));
    if (status === 'all') return byName;
    return byName.filter(row =>
      data.courses.some(c =>
        (data.matrix[row.id]?.[c.courseId]?.status || 'not_assigned') === status));
  }, [data, filter, status]);

  if (!data) return <div className="p-6 text-slate-500">Loading matrix…</div>;

  const cellFor = (empId, courseId) => {
    const cell = data.matrix?.[empId]?.[courseId];
    if (!cell) return { status: 'not_assigned' };
    return cell;
  };

  const downloadBulk = async () => {
    if (!bulkCourse) {
      toast.error('Pick a course first');
      return;
    }
    const [courseId, version] = bulkCourse.split('||');
    // Restrict to currently-visible employees. If the filter is empty
    // and status is "all", pass `null` so the backend returns every
    // employee with a certificate, which is what the admin expects.
    const filtering = filter.trim() !== '' || status !== 'all';
    const employeeIds = filtering ? visibleEmployees.map(e => e.id) : null;
    setBulkBusy(true);
    try {
      const { count } = await academyBulkCertificatesZip(courseId, version, employeeIds);
      toast.success(
        count
          ? `Exported ${count} certificate${count === '1' ? '' : 's'}`
          : 'Certificates exported',
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not export certificates');
    } finally {
      setBulkBusy(false);
    }
  };

  const deleteCert = async (emp, course, cell) => {
    if (!cell?.certNumber) return;
    if (!window.confirm(
      `Delete ${emp.name}'s certificate for ${course.title} v${course.version}?\n\n` +
      `Certificate number: ${cell.certNumber}\n\n` +
      `This removes the certificate from the Training Matrix and revokes the PDF. ` +
      `The learner's progress and assessment attempt are NOT changed — reassign the course ` +
      `if you want them to retake it.`
    )) return;
    try {
      await academyDeleteCertificate(cell.certNumber);
      toast.success('Certificate deleted');
      // Refresh so the cell falls back to its underlying assignment/progress state.
      setData(await academyTrainingMatrix());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete certificate');
    }
  };


  return (
    <div className="p-6 space-y-4" data-testid="training-matrix">
      <div className="flex items-start gap-2 flex-wrap justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-500 uppercase text-xs tracking-widest">
            <Grid3x3 className="h-3.5 w-3.5" />Lutrom Academy
          </div>
          <h1 className="text-2xl font-display font-bold">Training Matrix</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input placeholder="Filter employees…" value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
            data-testid="matrix-filter-employees" />
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="border rounded px-2 py-1 text-sm" data-testid="matrix-filter-status">
            <option value="all">All statuses</option>
            <option value="complete">Complete</option>
            <option value="in_progress">In progress</option>
            <option value="not_started">Not started</option>
            <option value="due_soon">Due soon</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>
      </div>

      {/* Bulk certificate export */}
      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-2"
        data-testid="matrix-bulk-cert-panel">
        <Package className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-medium text-slate-700">Bulk certificates:</span>
        <select
          value={bulkCourse}
          onChange={(e) => setBulkCourse(e.target.value)}
          className="border rounded px-2 py-1 text-sm min-w-[240px]"
          data-testid="matrix-bulk-cert-course"
        >
          <option value="">Select a course/version…</option>
          {data.courses.map((c) => (
            <option key={`${c.courseId}||${c.version}`} value={`${c.courseId}||${c.version}`}>
              {c.title} — v{c.version}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          onClick={downloadBulk}
          disabled={!bulkCourse || bulkBusy}
          data-testid="matrix-bulk-cert-download"
        >
          <Download className="h-3 w-3 mr-1" />
          {bulkBusy ? 'Preparing…' : 'Download ZIP'}
        </Button>
        <span className="text-xs text-slate-500 ml-1">
          {filter.trim() !== '' || status !== 'all'
            ? `Scope: ${visibleEmployees.length} filtered employee${visibleEmployees.length === 1 ? '' : 's'}`
            : 'Scope: everyone with a certificate'}
        </span>
      </div>

      <div className="overflow-x-auto border rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">Employee</th>
              {data.courses.map((c) => (
                <th key={c.courseId} className="px-2 py-2 text-left whitespace-nowrap">
                  <div className="font-semibold">{c.title}</div>
                  <div className="text-slate-400 text-[10px]">v{c.version} · {c.category}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleEmployees.map((emp) => (
              <tr key={emp.id} className="border-t hover:bg-slate-50">
                <td className="px-3 py-2 font-medium sticky left-0 bg-white">
                  {emp.name}
                  <div className="text-slate-500 text-[10px]">{emp.role}</div>
                </td>
                {data.courses.map((c) => {
                  const cell = cellFor(emp.id, c.courseId);
                  const cls = STATUS_COLORS[cell.status] || 'bg-white';
                  const canDeleteCert = cell.status === 'complete' && !!cell.certNumber;
                  return (
                    <td key={c.courseId} className="px-1 py-1">
                      <div className={`${cls} rounded px-2 py-1 text-center relative group`}
                        title={cell.certNumber ? `Cert ${cell.certNumber}` : JSON.stringify(cell)}
                        data-testid={`matrix-cell-${emp.id}-${c.courseId}`}>
                        {cell.status === 'not_assigned' ? '—' :
                         cell.status === 'complete' ? `✓ ${cell.score || 100}%` :
                         cell.status === 'in_progress' ? `${cell.percent}%` :
                         cell.status.replace('_', ' ')}
                        {canDeleteCert && (
                          <button
                            type="button"
                            onClick={() => deleteCert(emp, c, cell)}
                            aria-label="Delete certificate"
                            title="Delete certificate"
                            className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white border border-rose-300 text-rose-600 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-rose-600 hover:text-white transition-opacity shadow-sm"
                            data-testid={`matrix-cert-delete-${emp.id}-${c.courseId}`}
                          >
                            <X className="h-2.5 w-2.5" strokeWidth={3} />
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">Generated at {new Date(data.generatedAt).toLocaleString()} · {visibleEmployees.length} employees × {data.courses.length} courses</p>
    </div>
  );
}
